// src/api/admin/dashboard.js

import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Add request interceptor to include auth token in headers
API.interceptors.request.use(
  (config) => {
    const authData = JSON.parse(localStorage.getItem("authData") || "null");
    if (authData?.accessToken) {
      config.headers.Authorization = `Bearer ${authData.accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ===== Dashboard Analytics & Overview =====

/**
 * Get sales analytics data
 * @param {string} gender - 'male', 'female', or 'all'
 * @returns {Promise} Resolves to { success: true, data: [{ month, opdRevenue, ipdRevenue, totalRevenue }] }
 */
export const getSalesAnalytics = (gender = "all") =>
  API.get("/admin/sales-analytics", { params: { ...(gender !== "all" && { gender }) } });

/**
 * Get new patient registrations count
 * @param {string} type - 'monthly', 'weekly', or 'daily'
 * @param {string} gender - 'male', 'female', or 'all'
 * @returns {Promise} Resolves to { success: true, data: { monthly, weekly, daily } }
 */
export const getNewPatientRegistrations = (type = "monthly", gender = "all") =>
  API.get("/admin/new-patient-registrations", { params: { type, ...(gender !== "all" && { gender }) } });

/**
 * Get overall finance summary (overall revenue, collection, pending, and invoices)
 * @param {string} gender - 'male', 'female', or 'all'
 * @returns {Promise}
 */
export const getFinanceSummary = (gender = "all") =>
  API.get("/admin/finance-summary", { params: { ...(gender !== "all" && { gender }) } });

/**
 * Get breakdown of payments by mode (cash, card, upi, insurance)
 * @param {string} gender - 'male', 'female', or 'all'
 * @returns {Promise}
 */
export const getPaymentModesBreakdown = (gender = "all") =>
  API.get("/admin/payment-modes-breakdown", { params: { ...(gender !== "all" && { gender }) } });

/**
 * Get pending aging report
 * @param {string} gender - 'male', 'female', or 'all'
 * @returns {Promise}
 */
export const getPendingAgingReport = (gender = "all") =>
  API.get("/admin/pending-aging-report", { params: { ...(gender !== "all" && { gender }) } });

/**
 * Get IPD summary
 * @param {string} gender - 'male', 'female', or 'all'
 * @returns {Promise}
 */
export const getIpdSummary = (gender = "all") =>
  API.get("/admin/ipd-summary", { params: { ...(gender !== "all" && { gender }) } });

/**
 * Get dashboard overview
 * @param {string} gender - 'male', 'female', or 'all'
 * @returns {Promise}
 */
export const getDashboardOverview = (gender = "all") =>
  API.get("/admin/dashboard-overview", { params: { ...(gender !== "all" && { gender }) } });

/**
 * Get revenue analytics
 * @param {string} range 
 * @param {string} year 
 * @param {string} period 
 * @param {string} gender - 'male', 'female', or 'all'
 * @returns {Promise}
 */
export const getRevenueAnalytics = (range, year, period, gender = "all") =>
  API.get("/admin/revenue-analytics", { params: { range, year, period, ...(gender !== "all" && { gender }) } });

/**
 * Get OPD case count
 * @param {string} type 
 * @param {string} gender - 'male', 'female', or 'all'
 * @returns {Promise}
 */
export const getOpdCaseCount = (type = "monthly", gender = "all") =>
  API.get("/admin/opd-case-count", { params: { type, ...(gender !== "all" && { gender }) } });

/**
 * Get follow-up counts
 * @param {string} gender - 'male', 'female', or 'all'
 * @returns {Promise}
 */
export const getFollowUpCounts = (gender = "all") =>
  API.get("/admin/follow-up-counts", { params: { ...(gender !== "all" && { gender }) } });

/**
 * Get follow-up patients for a specific date
 * @param {string} date - 'YYYY-MM-DD'
 * @returns {Promise}
 */
export const getFollowUpPatients = (date) =>
  API.get("/admin/get-follow-up-patients", { params: { date } });

/**
 * Get campaign referral count
 * @returns {Promise} Resolves to { success: true, data: { campaignReferralCount: number } }
 */
export const getCampaignReferralCount = () =>
  API.get("/admin/campaign-referral-count");

/**
 * Get campaign referral patients with pagination
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 10)
 * @returns {Promise} Resolves to { success: true, data: { count, patients: [], page, totalPages } }
 */
export const getCampaignReferralPatients = (page = 1, pageSize = 10) =>
  API.get("/admin/campaign-referral-patients", { params: { page, pageSize } });

/**
 * Search campaign referral patients
 * @param {string} campaign_name - Campaign name filter (optional)
 * @param {string} query - Search query (optional)
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 10)
 * @returns {Promise} Resolves to { success: true, data: { count, patients: [], page, totalPages } }
 */
export const searchCampaignReferralPatients = (campaign_name = "", query = "", page = 1, pageSize = 10) =>
  API.get("/admin/campaign-referral-search", { params: { campaign_name, query, page, pageSize } });

/**
 * Update the sales status of a campaign referred patient
 * @param {string} patientId 
 * @param {string} salesStatus 
 * @returns {Promise}
 */
export const updateSalesStatus = (patientId, salesStatus, contactedStatus) =>
  API.put("/admin/update-sales-status", { patientId, salesStatus, contactedStatus });

export const getTodayCampReport = () => API.get("/admin/camp-daily-report/today");

export const getCampReport = (date) =>
  API.get("/admin/camp-daily-report", { params: date ? { date } : {} });

export const getMyCampReports = () => API.get("/admin/camp-daily-report/mine");

export const submitCampReport = (payload) => API.post("/admin/camp-daily-report", payload);

export const getCampReports = (date) =>
  API.get("/admin/camp-daily-reports", { params: date ? { date } : {} });